import { type OperationVariables, useLazyQuery, useQuery } from '@apollo/client';
import type { Theme } from '@mui/material';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useContext } from 'react';
import {
  INDUSTRY_TRACKER_TYPES,
  SECURE_REGION_DOWNLOAD_QUERY,
} from '@/components/Downloads/operations';
import OilRigIcon from '@/components/Icons/OilRigIcon';
import SnackbarContext from '@/contexts/SnackbarContext';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import { MaOpportunitiesList } from '@/pages/LandingPage/SideBar/IndustryTrackers/MaOpportunitiesList';
import { type IndustryTracker, IndustryTrackerTypeEnum } from '@/types/GraphqlSchema';
import { IndustryTrackerList } from './IndustryTrackerList';

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    ...theme.palette.secondaryBackground,
    alignItems: 'center',
    display: 'flex',
    padding: '5px 16px',
  },
  title: {
    paddingLeft: '10px',
  },
}));

export default () => {
  const classes = useStyles();
  const { setSnack } = useContext(SnackbarContext);
  const { inDevelopment } = useGetFeatureFlags();

  const downloadQueryOptions = useCallback(
    (dataKey: string): OperationVariables => ({
      fetchPolicy: 'network-only',
      onCompleted: (data: { [key: string]: { url: string; downloadMessage: string } }) => {
        const { url, downloadMessage } = data[dataKey] || { url: null, downloadMessage: '' };
        if (url) {
          setSnack({
            open: true,
            message: `Your download should begin shortly.${downloadMessage}`,
            severity: 'success',
          });
          window.location.href = url;
        }
      },
      onError: (error: Error) => {
        setSnack({
          severity: 'error',
          message: error.message,
          open: true,
        });
      },
    }),
    [setSnack],
  );

  const [getSecureRegionDownload] = useLazyQuery(
    SECURE_REGION_DOWNLOAD_QUERY,
    downloadQueryOptions('secureRegionDownload'),
  );

  const { data: industryTrackerDownloadTypesData } = useQuery(INDUSTRY_TRACKER_TYPES, {
    fetchPolicy: 'cache-and-network',
  });
  const industryTrackerDownloadTypes =
    industryTrackerDownloadTypesData?.industryTrackerDownloadTypes as IndustryTracker[] | null;

  const onIndustryTrackerClick = (
    type: IndustryTrackerTypeEnum,
    region: string,
    subType: string,
  ) => {
    getSecureRegionDownload({
      variables: { downloadType: type, region, subType },
    });
  };

  return (
    <>
      {/* biome-ignore lint/correctness/useUniqueElementIds: we need an ID for the tutorial */}
      <div id='industry-tracker-accordion' className={classes.header}>
        <OilRigIcon sx={{ fontSize: '24px' }} />
        <Typography className={classes.title} variant='h6'>
          Industry Trackers
        </Typography>
      </div>
      <Divider />
      <MaOpportunitiesList />
      {industryTrackerDownloadTypes
        ?.filter(({ type }) => inDevelopment || type !== IndustryTrackerTypeEnum.LngFacilities)
        ?.map(industryTrackerDownloadType => (
          <IndustryTrackerList
            key={industryTrackerDownloadType.name}
            industryTracker={industryTrackerDownloadType}
            onClick={onIndustryTrackerClick}
          />
        ))}
    </>
  );
};
